import { operationDefinition, accessPolicyTypeDefinition } from "pages/System/RealmSetting/components/Wired/const";

export const syncPortLabels = (port) => {
  const opType = port?.operation?.type || "switching";
  const groupId = port?.operation?.aggregateGroupId;
  let formattedAggregateLabel = "-";
  if (opType === "aggregation") {
    formattedAggregateLabel = groupId != null ? `Aggregate #${groupId}` : "Aggregate";
  } else {
    formattedAggregateLabel = "-";
  }

  const accessPolicies = port?.accessPolicy?.enableState
    ? (accessPolicyTypeDefinition[port?.accessPolicy?.type]?.label ?? "-")
    : accessPolicyTypeDefinition.DISABLED.label;

  const formattedRSTPLabel = port.rstpState ? "Enabled" : "Disabled";
  const stpGuard = port.stpState ? "Enabled" : "Disabled";
  const formattedLBDStateLabel = port.lbdState ? "Enabled" : "Disabled";
  const formattedDDPStateLabel = port.ddpState ? "Enabled" : "Disabled";

  const portSchedule = port.isPortScheduleMode && port.portSchedule ? port.portSchedule : "-";
  const poeSchedule = port.isPoeScheduleMode && port.poeSchedule ? port.poeSchedule : "-";

  // mirror column:
  let mirror = "-";
  if (opType === "mirroring") {
    const mirrorModeLabel = port?.operation?.mirrorMode === "tx" ? "TX" : (port?.operation?.mirrorMode === "rx" ? "RX" : "Both");
    const mirrorPorts = port?.operation?.mirrorPort || [];
    mirror = mirrorPorts.length > 0 ? `${mirrorModeLabel} (Port ${mirrorPorts.join(", ")})` : mirrorModeLabel;
  }

  return {
    ...port,
    formattedAggregateLabel,
    accessPolicies,
    formattedRSTPLabel,
    stpGuard,
    formattedLBDStateLabel,
    formattedDDPStateLabel,
    mirror,
    portSchedule,
    poeSchedule,
  };
};

export const getUpdatedAssociatedPorts = ({
  switchesData,
  activeRow,
  originalRowData,
  updatedPort,
  excludeIndices = []
}) => {
  const activeIdx = +activeRow.portIndex;

  const { type: oldType = "switching", mirrorPort: oldMirrorPorts = [], aggregatePort: oldAggPorts = [] } = originalRowData.operation || {};
  const { type: newType = "switching", mirrorPort: newMirrorPorts = [], aggregatePort: newAggPorts = [] } = updatedPort.operation || {};

  const newMirrorGroup = [activeIdx, ...newMirrorPorts]; // [ self, ...associated mirror ports ]
  const newAggregateGroup = [activeIdx, ...newAggPorts]; // [ self, ...associated aggregate ports ]

  return switchesData.map(loopSwitch => {
    const portIdx = +loopSwitch.portIndex;

    // User 正在直接編輯的 selectedPort.
    if (portIdx === activeIdx) {
      return syncPortLabels({
        ...updatedPort,
        isWaitingEdit: true
      });
    }

    // 如果該 port 是本次批次編輯的其中一個 port，則跳過同步，避免被舊狀態覆寫.
    if (excludeIndices.includes(portIdx)) {
      return loopSwitch;
    }

    let op = { ...loopSwitch.operation };
    let changed = false;

    // 1. 切到 mirror mode 時.
    if (newType === "mirroring") {
      if (newMirrorPorts.includes(portIdx)) {
        // 被 mirrror 的 source ports 設為 switching.
        op.type = "switching";
        op.mirrorPort = [];
        op.mirrorMode = "";
        op.aggregatePort = [];
        op.aggregateGroupId = null;
        op.aggregateType = "switching";
        changed = true;
      } else if (loopSwitch.operation?.type === "mirroring" && portIdx !== activeIdx) {
        // 清除裝置上其他已存在的 mirror port (保持一個 device 只有一組 mirror group).
        op.type = "switching";
        op.mirrorPort = [];
        op.mirrorMode = "";
        op.aggregatePort = [];
        op.aggregateGroupId = null;
        op.aggregateType = "switching";
        changed = true;
      }
    }

    // 2. 切到 aggregation mode 時.
    else if (newType === "aggregation") {
      if (newAggregateGroup.includes(portIdx)) {
        // 剛被加入到 aggregation group 或同組成員.
        op.type = "aggregation";
        op.aggregatePort = newAggregateGroup.filter(id => id !== portIdx); // 排除自己.
        op.aggregateGroupId = updatedPort.operation.aggregateGroupId;
        op.aggregateType = updatedPort.operation.aggregateType;
        op.mirrorPort = [];
        op.mirrorMode = "";

        // 只有 aggregation mode 時才同步所有 port 設定
        return syncPortLabels({
          ...loopSwitch,
          portProfile: updatedPort.portProfile,
          portState: updatedPort.portState,
          portType: updatedPort.portType,
          PVID: updatedPort.PVID,
          allowVlan: updatedPort.allowVlan,
          speed: updatedPort.speed,
          comboSfpSpeed: updatedPort.comboSfpSpeed,
          poeState: updatedPort.poeState,
          ddpState: updatedPort.ddpState,
          lbdState: updatedPort.lbdState,
          rstpState: updatedPort.rstpState,
          stpState: updatedPort.stpState,
          isPortScheduleMode: updatedPort.isPortScheduleMode,
          isPoeScheduleMode: updatedPort.isPoeScheduleMode,
          portSchedule: updatedPort.portSchedule,
          poeSchedule: updatedPort.poeSchedule,
          accessPolicy: updatedPort.accessPolicy,
          ...(updatedPort.portTags !== undefined && { portTags: updatedPort.portTags }),
          ...(updatedPort.tags !== undefined && { tags: updatedPort.tags }),
          ...(updatedPort.combinePortName !== undefined && { combinePortName: updatedPort.combinePortName }),
          operation: op,
          isWaitingEdit: true
        });
      } else if (oldType === "aggregation" && oldAggPorts.includes(portIdx)) {
        // 切成 switching mode, 被移出 aggregation group.
        op.type = "switching";
        op.aggregatePort = [];
        op.aggregateGroupId = null;
        op.aggregateType = "switching";
        changed = true;
      }

      // 如果該 port 原本是 mirror port，且它的群組成員（或是它自己）被拉去 aggregation，則清除或更新 mirror group.
      if (loopSwitch.operation?.type === "mirroring" && portIdx !== activeIdx) {
        if (newAggregateGroup.includes(portIdx)) {
          //  mirror port 本身被拉去 aggregation -> mirror group 解散.
          op.type = "switching";
          op.mirrorPort = [];
          op.mirrorMode = "";
          changed = true;
        } else {
          // 檢查是否有被 mirror 的 source ports 被拉去 aggregation.
          const currentMirror = loopSwitch.operation.mirrorPort || [];
          const updatedMirror = currentMirror.filter(id => !newAggregateGroup.includes(id));
          if (updatedMirror.length !== currentMirror.length) {
            if (updatedMirror.length === 0) {
              op.type = "switching";
              op.mirrorPort = [];
              op.mirrorMode = "";
            } else {
              op.mirrorPort = updatedMirror;
            }
            changed = true;
          }
        }
      }
    }

    // 3. 切到 switching mode 時.
    else {
      // 從 aggregation group 中移除 port.
      if (oldType === "aggregation" && oldAggPorts.includes(portIdx)) {
        const hasRemainingGroup = oldAggPorts.length >= 2; // 當 group 中至少還有2個 ports, 是合法的.
        op.type = hasRemainingGroup ? "aggregation" : "switching"; // 合法的就保持原 mode.
        op.aggregatePort = hasRemainingGroup ? oldAggPorts.filter(id => id !== portIdx) : [];
        op.aggregateGroupId = hasRemainingGroup ? op.aggregateGroupId : null;
        op.aggregateType = hasRemainingGroup ? op.aggregateType : "switching";
        changed = true;
      }
    }

    if (changed) {
      return syncPortLabels({
        ...loopSwitch,
        operation: op,
        isWaitingEdit: true
      });
    }

    return loopSwitch;
  });
};

export const checkPortInOtherAggregateGroup = ({
  portItem,
  selectedIndexList,
  switchesData
}) => {
  if (selectedIndexList.includes(portItem.portIndex)) return false;

  return switchesData.some(port => {
    if (selectedIndexList.includes(port.portIndex)) return false;
    if (port.operation?.type !== "aggregation") return false;

    const aggregationPorts = port.operation?.aggregatePort || [];
    const isSameGroup = aggregationPorts.some(id => selectedIndexList.includes(id)) || selectedIndexList.includes(Number(port.portIndex));
    if (isSameGroup) {
      return false;
    }

    return port.portIndex === portItem.portIndex || aggregationPorts.includes(portItem.portIndex);
  });
};

// Returns null if no group id is available.
export const getAvailableAggregateGroupId = (portConfigs, existingId, maxGroups) => {
  if (existingId != null) return existingId;

  const usedIds = new Set(
    portConfigs
      .map(port => port.operation?.aggregateGroupId)
      .filter(id => id != null)
  );

  const candidates = [];
  for (let i = 1; i <= (maxGroups || 0); i++) {
    if (!usedIds.has(i)) candidates.push(i);
  }

  if (candidates.length === 0) return null;

  return candidates[Math.floor(Math.random() * candidates.length)];
};
